import React, { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Trash2,
  Save,
  Type,
  Hash,
  ToggleLeft,
  Edit2,
  Loader,
  Info,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Globe
} from 'lucide-react';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

export interface AttributeOption {
  value: string;
  label: string;
}

export interface CategoryAttribute {
  id: string;
  name: string;
  type: 'text' | 'number' | 'boolean';
  options?: AttributeOption[];
  restrictToOptions?: boolean;
  unit?: string; // Para números (ej: "Lt", "Kg", "m²")
  placeholder?: string;
  order: number;
}

interface AttributesModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string;
  categoryName: string;
  clientId: string;
  inheritedAttributes?: CategoryAttribute[]; // Atributos heredados del padre
}

const AttributesModal: React.FC<AttributesModalProps> = ({
  isOpen,
  onClose,
  categoryId,
  categoryName,
  clientId,
  inheritedAttributes = []
}) => {
  const [attributes, setAttributes] = useState<CategoryAttribute[]>([]);
  const [globalAttributes, setGlobalAttributes] = useState<CategoryAttribute[]>([]);
  const [newAttribute, setNewAttribute] = useState<Partial<CategoryAttribute>>({
    type: 'text',
    restrictToOptions: false,
    options: []
  });
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedAttributes, setExpandedAttributes] = useState<Set<string>>(new Set());
  const [showGlobalAttributes, setShowGlobalAttributes] = useState(true);

  // Cargar atributos desde Firestore
  useEffect(() => {
    if (!isOpen) return;

    const loadAttributes = async () => {
      setLoading(true);
      try {
        // Cargar atributos de la categoría
        const docRef = doc(db, 'clients', clientId, 'categories', categoryId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setAttributes(data.attributes || []);
        } else {
          setAttributes([]);
        }

        // Cargar atributos globales
        const globalDocRef = doc(db, 'clients', clientId, 'catalogRules', 'globalAttributes');
        const globalDocSnap = await getDoc(globalDocRef);
        
        if (globalDocSnap.exists()) {
          const globalData = globalDocSnap.data();
          const globalAttrs = (globalData.attributes || []).map((attr: any, index: number) => ({
            id: `global_${index}`,
            name: attr.name,
            type: attr.type || 'text',
            options: attr.options?.map((opt: string) => ({ value: opt, label: opt })) || [],
            restrictToOptions: attr.type === 'select' || attr.type === 'multiselect',
            unit: '',
            placeholder: attr.description || '',
            order: index,
            isGlobal: true
          }));
          setGlobalAttributes(globalAttrs);
        } else {
          setGlobalAttributes([]);
        }
      } catch (error) {
        console.error('Error loading attributes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAttributes();
  }, [isOpen, categoryId, clientId]);

  // Guardar atributos en Firestore
  const saveAttributes = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'clients', clientId, 'categories', categoryId);
      
      // Serializar atributos para Firestore
      const serializedAttributes = attributes.map(attr => ({
        id: attr.id,
        name: attr.name,
        type: attr.type,
        options: attr.options || [],
        restrictToOptions: attr.restrictToOptions || false,
        unit: attr.unit || '',
        placeholder: attr.placeholder || '',
        order: attr.order
      }));
      
      await setDoc(docRef, {
        attributes: serializedAttributes,
        attributesUpdatedAt: Timestamp.now()
      }, { merge: true });
      
      onClose();
    } catch (error) {
      console.error('Error saving attributes:', error);
      alert('Error al guardar los atributos');
    } finally {
      setSaving(false);
    }
  };

  // Agregar nuevo atributo
  const addAttribute = () => {
    if (!newAttribute.name?.trim()) {
      alert('El nombre del atributo es requerido');
      return;
    }

    // Verificar que no exista un atributo con el mismo nombre
    if (attributes.some(attr => attr.name.toLowerCase() === newAttribute.name!.toLowerCase())) {
      alert('Ya existe un atributo con ese nombre');
      return;
    }

    const attribute: CategoryAttribute = {
      id: `attr-${Date.now()}`,
      name: newAttribute.name!,
      type: newAttribute.type || 'text',
      restrictToOptions: newAttribute.restrictToOptions || false,
      options: newAttribute.options || [],
      unit: newAttribute.unit,
      placeholder: newAttribute.placeholder,
      order: attributes.length
    };

    setAttributes([...attributes, attribute]);
    setNewAttribute({
      type: 'text',
      restrictToOptions: false,
      options: []
    });
    setShowNewForm(false);
  };

  // Eliminar atributo
  const deleteAttribute = (id: string) => {
    if (window.confirm('¿Eliminar este atributo?')) {
      setAttributes(attributes.filter(attr => attr.id !== id));
    }
  };

  // Actualizar atributo
  const updateAttribute = (id: string, updates: Partial<CategoryAttribute>) => {
    setAttributes(attributes.map(attr => 
      attr.id === id ? { ...attr, ...updates } : attr
    ));
  };

  // Agregar opción a un atributo select/multiselect
  const addOptionToAttribute = (attrId: string, value: string) => {
    if (!value.trim()) return;
    
    const attribute = attributes.find(attr => attr.id === attrId);
    if (!attribute) return;

    // Normalizar el valor a Title Case (Primera letra mayúscula)
    const normalizedValue = value.trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

    // Verificar que no exista la opción
    if (attribute.options?.some(opt => opt.value.toLowerCase() === normalizedValue.toLowerCase())) {
      alert('Ya existe una opción con ese valor');
      return;
    }

    const newOption: AttributeOption = {
      value: normalizedValue,
      label: normalizedValue
    };

    updateAttribute(attrId, {
      options: [...(attribute.options || []), newOption]
    });
  };

  // Eliminar opción de un atributo
  const removeOption = (attrId: string, optionValue: string) => {
    const attribute = attributes.find(attr => attr.id === attrId);
    if (!attribute) return;

    updateAttribute(attrId, {
      options: attribute.options?.filter(opt => opt.value !== optionValue)
    });
  };

  // Obtener icono según el tipo
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'text': return <Type className="w-4 h-4" />;
      case 'number': return <Hash className="w-4 h-4" />;
      case 'boolean': return <ToggleLeft className="w-4 h-4" />;
      default: return <Type className="w-4 h-4" />;
    }
  };

  // Toggle expandir/colapsar atributo
  const toggleAttributeExpanded = (attrId: string) => {
    setExpandedAttributes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(attrId)) {
        newSet.delete(attrId);
      } else {
        newSet.add(attrId);
      }
      return newSet;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-4xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Atributos de la Categoría
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {categoryName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {loading ? (
            <div className="py-12 text-center">
              <Loader className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Cargando atributos...</p>
            </div>
          ) : (
            <>
              {/* Atributos globales */}
              {globalAttributes.length > 0 && (
                <div className="mb-6">
                  <div 
                    className="flex items-center justify-between cursor-pointer mb-3"
                    onClick={() => setShowGlobalAttributes(!showGlobalAttributes)}
                  >
                    <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-purple-600" />
                      Atributos Globales ({globalAttributes.length})
                    </h4>
                    {showGlobalAttributes ? 
                      <ChevronDown className="w-4 h-4 text-gray-500" /> : 
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    }
                  </div>
                  {showGlobalAttributes && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
                      {globalAttributes.map(attr => (
                        <div key={attr.id} className="flex items-center justify-between text-sm">
                          <div className="flex items-center text-purple-900">
                            {getTypeIcon(attr.type)}
                            <span className="ml-2 font-medium">{attr.name}</span>
                            <span className="ml-2 text-purple-600 text-xs">
                              ({attr.type === 'text' ? 'Texto' : 
                                attr.type === 'number' ? 'Número' : 
                                attr.type === 'boolean' ? 'Sí/No' :
                                attr.type === 'select' ? 'Selección' : 
                                'Multi-selección'})
                            </span>
                          </div>
                          {attr.options && attr.options.length > 0 && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                              {attr.options.length} opciones
                            </span>
                          )}
                        </div>
                      ))}
                      <div className="text-xs text-purple-700 mt-2 pt-2 border-t border-purple-200">
                        <Info className="w-3 h-3 inline mr-1" />
                        Estos atributos se aplican a todas las categorías automáticamente
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Atributos heredados */}
              {inheritedAttributes.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Atributos heredados del padre
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    {inheritedAttributes.map(attr => (
                      <div key={attr.id} className="flex items-center text-sm text-gray-600">
                        {getTypeIcon(attr.type)}
                        <span className="ml-2">{attr.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lista de atributos */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {attributes.map((attr) => {
                  const isExpanded = expandedAttributes.has(attr.id);
                  const optionsCount = attr.options?.length || 0;
                  
                  return (
                  <div key={attr.id} className="border border-gray-200 rounded-lg">
                    {/* Header del atributo - siempre visible */}
                    <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                         onClick={() => toggleAttributeExpanded(attr.id)}>
                      <div className="flex items-center flex-1">
                        <button className="mr-2">
                          {isExpanded ? 
                            <ChevronDown className="w-4 h-4 text-gray-500" /> : 
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          }
                        </button>
                        {getTypeIcon(attr.type)}
                        <span className="ml-2 font-medium text-gray-900">{attr.name}</span>
                        {editingAttribute === attr.id ? (
                          <input
                            type="text"
                            value={attr.name}
                            onChange={(e) => updateAttribute(attr.id, { name: e.target.value })}
                            onBlur={() => setEditingAttribute(null)}
                            onKeyPress={(e) => e.key === 'Enter' && setEditingAttribute(null)}
                            onClick={(e) => e.stopPropagation()}
                            className="ml-2 font-medium text-gray-900 border-b border-blue-500 focus:outline-none"
                            autoFocus
                          />
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingAttribute(attr.id);
                            }}
                            className="ml-2 text-gray-400 hover:text-gray-600"
                          >
                            <Edit2 className="w-3 h-3" />
                          </button>
                        )}
                        
                        {/* Vista compacta cuando está colapsado */}
                        {!isExpanded && (
                          <div className="flex items-center ml-auto text-sm text-gray-500">
                            <span className="mr-3">{attr.type === 'text' ? 'Texto' : attr.type === 'number' ? 'Numérico' : 'Booleano'}</span>
                            {optionsCount > 0 && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                                {optionsCount} {optionsCount === 1 ? 'valor' : 'valores'}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Botón de eliminar */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteAttribute(attr.id);
                        }}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Contenido expandible */}
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-gray-100">
                        <div className="mt-3 space-y-2">
                          {/* Tipo y configuración */}
                          <div className="flex flex-wrap gap-3 text-sm">
                            <select
                              value={attr.type}
                              onChange={(e) => updateAttribute(attr.id, { 
                                type: e.target.value as CategoryAttribute['type']
                              })}
                              className="px-2 py-1 border border-gray-300 rounded"
                            >
                              <option value="text">Texto</option>
                              <option value="number">Numérico</option>
                              <option value="boolean">Booleano</option>
                            </select>

                            {attr.type === 'number' && (
                              <input
                                type="text"
                                placeholder="Unidad (ej: Lt, Kg)"
                                value={attr.unit || ''}
                                onChange={(e) => updateAttribute(attr.id, { unit: e.target.value })}
                                className="px-2 py-1 border border-gray-300 rounded w-24"
                              />
                            )}
                          </div>

                          {/* Valores posibles solo para texto y número */}
                          {(attr.type === 'text' || attr.type === 'number') && (
                          <div className="mt-3 p-3 bg-gray-50 rounded">
                            <div className="mb-2">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">
                                  Valores posibles
                                </span>
                                <label className="flex items-center text-sm">
                                  <input
                                    type="checkbox"
                                    checked={attr.restrictToOptions}
                                    onChange={(e) => updateAttribute(attr.id, { restrictToOptions: e.target.checked })}
                                    className="mr-1"
                                  />
                                  <span>Limitar catalogado a estos valores</span>
                                </label>
                              </div>
                              {attr.restrictToOptions && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Los valores se limitarán a la lista definida. Si el valor no coincide, se asignará "Otro".
                                </p>
                              )}
                              {!attr.restrictToOptions && attr.options && attr.options.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Los valores son sugerencias. Se pueden usar otros valores no listados.
                                </p>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap gap-2 min-h-[32px]">
                              {attr.options?.map((option) => (
                                <span 
                                  key={option.value} 
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-700 group hover:bg-blue-100 hover:border-blue-300 transition-colors"
                                >
                                  <span>{option.label}</span>
                                  <button
                                    onClick={() => removeOption(attr.id, option.value)}
                                    className="ml-1 text-blue-400 hover:text-red-500 transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                              {attr.restrictToOptions && attr.options && attr.options.length > 0 && (
                                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 border border-gray-300 rounded-full text-sm italic text-gray-600">
                                  Otro
                                </span>
                              )}
                              {attr.options?.length === 0 && (
                                <span className="text-xs text-gray-400 italic py-1">
                                  No hay valores definidos
                                </span>
                              )}
                            </div>

                            <div className="mt-2 flex gap-2">
                              <input
                                type="text"
                                placeholder={attr.type === 'number' ? 'Nuevo valor (ej: 100, 250, 500)' : 'Nuevo valor'}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === 'Tab') {
                                    e.preventDefault();
                                    const value = (e.target as HTMLInputElement).value.trim();
                                    if (value) {
                                      addOptionToAttribute(attr.id, value);
                                      (e.target as HTMLInputElement).value = '';
                                    }
                                  }
                                }}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                              <button
                                onClick={(e) => {
                                  const input = e.currentTarget.previousSibling as HTMLInputElement;
                                  const value = input.value.trim();
                                  if (value) {
                                    addOptionToAttribute(attr.id, value);
                                    input.value = '';
                                    input.focus();
                                  }
                                }}
                                className="px-2 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          )}

                          {/* Placeholder */}
                          <input
                            type="text"
                            placeholder="Texto de ayuda (opcional)"
                            value={attr.placeholder || ''}
                            onChange={(e) => updateAttribute(attr.id, { placeholder: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
                })}

                {attributes.length === 0 && !showNewForm && (
                  <div className="text-center py-8 text-gray-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No hay atributos definidos</p>
                  </div>
                )}

                {/* Formulario nuevo atributo */}
                {showNewForm && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Nuevo Atributo</h4>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Nombre del atributo"
                        value={newAttribute.name || ''}
                        onChange={(e) => setNewAttribute({ ...newAttribute, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        autoFocus
                      />
                      
                      <div className="flex gap-3">
                        <select
                          value={newAttribute.type}
                          onChange={(e) => setNewAttribute({ ...newAttribute, type: e.target.value as CategoryAttribute['type'] })}
                          className="px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="text">Texto</option>
                          <option value="number">Numérico</option>
                          <option value="boolean">Booleano</option>
                        </select>

                        {newAttribute.type === 'number' && (
                          <input
                            type="text"
                            placeholder="Unidad (ej: Lt, Kg)"
                            value={newAttribute.unit || ''}
                            onChange={(e) => setNewAttribute({ ...newAttribute, unit: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg w-32"
                          />
                        )}
                      </div>

                      {/* Valores posibles solo para texto y número */}
                      {(newAttribute.type === 'text' || newAttribute.type === 'number') && (
                      <div className="p-3 bg-white rounded-lg border border-gray-200">
                        <div className="mb-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">
                              Valores posibles
                            </span>
                            <label className="flex items-center text-sm">
                              <input
                                type="checkbox"
                                checked={newAttribute.restrictToOptions || false}
                                onChange={(e) => setNewAttribute({ ...newAttribute, restrictToOptions: e.target.checked })}
                                className="mr-1"
                              />
                              <span>Limitar catalogado</span>
                            </label>
                          </div>
                          {newAttribute.restrictToOptions && (
                            <p className="text-xs text-gray-500 mt-1">
                              Los valores se limitarán a la lista. Si no coincide, se asignará "Otro".
                            </p>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-2 min-h-[32px] mb-2">
                          {newAttribute.options?.map((option, index) => (
                            <span 
                              key={index} 
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-700 group hover:bg-blue-100 hover:border-blue-300 transition-colors"
                            >
                              <span>{option.label}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setNewAttribute({
                                    ...newAttribute,
                                    options: newAttribute.options?.filter((_, i) => i !== index)
                                  });
                                }}
                                className="ml-1 text-blue-400 hover:text-red-500 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                          {newAttribute.restrictToOptions && newAttribute.options && newAttribute.options.length > 0 && (
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 border border-gray-300 rounded-full text-sm italic text-gray-600">
                              Otro
                            </span>
                          )}
                          {(!newAttribute.options || newAttribute.options.length === 0) && (
                            <span className="text-xs text-gray-400 italic py-1">
                              No hay valores definidos (opcional)
                            </span>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder={newAttribute.type === 'number' ? 'Agregar valor (ej: 100)' : 'Agregar valor'}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === 'Tab') {
                                e.preventDefault();
                                e.stopPropagation();
                                const value = (e.target as HTMLInputElement).value.trim();
                                if (value) {
                                  // Normalizar el valor a Title Case
                                  const normalizedValue = value
                                    .toLowerCase()
                                    .replace(/\b\w/g, (char) => char.toUpperCase());
                                  
                                  // Verificar que no exista ya
                                  if (!newAttribute.options?.some(opt => opt.value.toLowerCase() === normalizedValue.toLowerCase())) {
                                    setNewAttribute({
                                      ...newAttribute,
                                      options: [...(newAttribute.options || []), { value: normalizedValue, label: normalizedValue }]
                                    });
                                    (e.target as HTMLInputElement).value = '';
                                  }
                                }
                              }
                            }}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              const input = e.currentTarget.previousSibling as HTMLInputElement;
                              const value = input.value.trim();
                              if (value) {
                                // Normalizar el valor a Title Case
                                const normalizedValue = value
                                  .toLowerCase()
                                  .replace(/\b\w/g, (char) => char.toUpperCase());
                                
                                // Verificar que no exista ya
                                if (!newAttribute.options?.some(opt => opt.value.toLowerCase() === normalizedValue.toLowerCase())) {
                                  setNewAttribute({
                                    ...newAttribute,
                                    options: [...(newAttribute.options || []), { value: normalizedValue, label: normalizedValue }]
                                  });
                                  input.value = '';
                                  input.focus();
                                }
                              }
                            }}
                            className="px-2 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      )}

                      <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewForm(false);
                            setNewAttribute({ type: 'text', restrictToOptions: false, options: [] });
                          }}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={addAttribute}
                          className="px-3 py-1.5 text-sm text-green-600 border border-green-600 rounded hover:bg-green-50 transition-colors"
                        >
                          Agregar Atributo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="mt-6 flex justify-between">
                {!showNewForm && (
                  <button
                    onClick={() => setShowNewForm(true)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Nuevo Atributo
                  </button>
                )}
                {showNewForm && <div />}

                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={saveAttributes}
                    className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg ${
                      saving 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Guardar
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttributesModal;